/**
 * Native integration — Firebase Messaging + RevenueCat.
 *
 * The whole module is a graceful no-op in the browser. When Capacitor reports
 * a native platform (iOS / Android shell), we wire up:
 *   - FCM permission prompt + token retrieval → POST /users/me/device-token
 *   - RevenueCat SDK init with appUserID = user.id
 *   - status bar + splash screen handoff
 *
 * Callers:
 *   - bootNativeOnce(user) — called once after auth in AuthContext
 *   - shutdownNative()     — called on logout
 *   - purchasePackage(pkg) — called from the paywall sheet on mobile
 *   - getOfferings()       — called from the paywall sheet on mobile
 */
import { Capacitor } from "@capacitor/core";
import { api } from "./api";

// RevenueCat public keys (NOT secrets — safe to bundle).
// Filled in once iOS/Android apps are added to the RC dashboard.
const RC_IOS_PUBLIC_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
const RC_ANDROID_PUBLIC_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";

let _booted = false;

export function isNative() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

export function platform() {
  try {
    const p = Capacitor.getPlatform();
    return p === "ios" || p === "android" ? p : "web";
  } catch {
    return "web";
  }
}

/**
 * Boot every native integration. Idempotent.
 */
export async function bootNativeOnce(user) {
  if (!isNative() || _booted) return;
  if (!user || !user.id) return;
  _booted = true;
  await Promise.allSettled([
    _bootCrashlytics(user),
    _bootFcm(user.id),
    _bootRevenueCat(user.id),
    _polishUi(),
  ]);
}

export async function shutdownNative() {
  if (!isNative()) return;
  _booted = false;
  try {
    const { cancelAllLocalReminders } = await import("./localNotifications");
    await cancelAllLocalReminders();
  } catch { /* best effort */ }
  try {
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    const { token } = await FirebaseMessaging.getToken();
    if (token) {
      await api.delete("/users/me/device-token", {
        data: { fcm_token: token, platform: platform() },
      });
    }
    await FirebaseMessaging.deleteToken();
  } catch {
    /* best effort */
  }
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    await Purchases.logOut();
  } catch {
    /* best effort */
  }
}

// ---------- Crashlytics ----------

async function _bootCrashlytics(user) {
  try {
    const { FirebaseCrashlytics } = await import("@capacitor-firebase/crashlytics");
    await FirebaseCrashlytics.setEnabled({ enabled: true });
    if (user?.id) {
      await FirebaseCrashlytics.setUserId({ userId: String(user.id) });
    }
    if (user?.email) {
      await FirebaseCrashlytics.setCustomKey({
        key: "email",
        value: String(user.email),
        type: "string",
      });
    }
    // Expose a window helper so QA can force a native crash from the JS
    // console to verify Crashlytics is wired end-to-end. Calling crash()
    // raises a fatal signal on the native side that Crashlytics will
    // capture on next launch.
    if (typeof window !== "undefined") {
      window.__kinlyForceCrash = async () => {
        await FirebaseCrashlytics.crash({ message: "Kinly QA verification crash" });
      };
    }
  } catch (e) {
    console.warn("[Kinly] Crashlytics boot failed:", e);
  }
}

// ---------- FCM ----------

async function _bootFcm(/* userId */) {
  try {
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");

    const perm = await FirebaseMessaging.requestPermissions();
    if (perm.receive !== "granted") {
      console.info("[Kinly] push permission not granted; skipping token registration");
      return;
    }

    const { token } = await FirebaseMessaging.getToken();
    if (token) {
      await api.post("/users/me/device-token", {
        fcm_token: token,
        platform: platform(),
      });
    }

    await FirebaseMessaging.addListener("notificationReceived", (event) => {
      console.info("[Kinly] push received (foreground):", event);
    });
    await FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
      console.info("[Kinly] push tapped:", event);
      // TODO: deep-link based on event.notification.data.kind
    });
    await FirebaseMessaging.addListener("tokenReceived", async (event) => {
      if (event?.token) {
        try {
          await api.post("/users/me/device-token", {
            fcm_token: event.token,
            platform: platform(),
          });
        } catch (e) {
          console.warn("[Kinly] token refresh registration failed:", e);
        }
      }
    });
  } catch (e) {
    console.warn("[Kinly] FCM boot failed:", e);
  }
}

// ---------- RevenueCat ----------

async function _bootRevenueCat(userId) {
  const apiKey = platform() === "ios" ? RC_IOS_PUBLIC_KEY : RC_ANDROID_PUBLIC_KEY;
  if (!apiKey) {
    console.info("[Kinly] RevenueCat key not configured for this platform; skipping");
    return;
  }
  try {
    const { Purchases, LOG_LEVEL } = await import("@revenuecat/purchases-capacitor");
    await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
    await Purchases.configure({ apiKey, appUserID: userId });
    // ─── Launch diagnostic ──────────────────────────────────────────────
    // Log the current offering's package identifiers so QA can verify
    // the RC dashboard → app contract on first TestFlight launch BEFORE
    // a real purchase is attempted. The PaywallSheet purchase flow
    // matches `pkg.identifier === tier.key` first, falling back to
    // `pkg.product.identifier === "kinly_{tier}_monthly"`. Look for
    // "[Kinly RC]" in Xcode console.
    try {
      const result = await Purchases.getOfferings();
      const offering = result?.current;
      const pkgs = (offering?.availablePackages || []).map(p => ({
        id: p.identifier,
        storeId: p?.product?.identifier,
        price: p?.product?.priceString,
      }));
      console.info("[Kinly RC] offering=", offering?.identifier, "packages=", JSON.stringify(pkgs));
      const expected = ["spark", "lite", "plus", "family"];
      const got = pkgs.map(p => p.id);
      const missing = expected.filter(t => !got.includes(t));
      if (missing.length > 0) {
        console.warn("[Kinly RC] MISSING expected packages:", missing, "— defensive fallback to kinly_{tier}_monthly will be used by paywall.");
      }
    } catch (e) {
      console.warn("[Kinly RC] launch-diagnostic getOfferings failed:", e?.message || e);
    }
  } catch (e) {
    console.warn("[Kinly] RevenueCat boot failed:", e);
  }
}

/** Fetch the current Offerings. Returns null on web or if RC isn't configured. */
export async function getOfferings() {
  if (!isNative()) return null;
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const result = await Purchases.getOfferings();
    return result.current || null;
  } catch (e) {
    console.warn("[Kinly] getOfferings failed:", e);
    return null;
  }
}

/** Trigger an in-app purchase. After success, ask backend to reconcile. */
export async function purchasePackage(pkg) {
  if (!isNative()) throw new Error("Purchases only work in the native app");
  const { Purchases } = await import("@revenuecat/purchases-capacitor");
  const result = await Purchases.purchasePackage({ aPackage: pkg });
  try {
    await api.post("/billing/refresh");
  } catch (e) {
    console.info("[Kinly] post-purchase refresh failed (non-fatal):", e);
  }
  return result;
}

/** "Restore purchases" — for reinstalls or device switches. */
export async function restorePurchases() {
  if (!isNative()) return null;
  const { Purchases } = await import("@revenuecat/purchases-capacitor");
  const customer = await Purchases.restorePurchases();
  try {
    await api.post("/billing/refresh");
  } catch {
    /* non-fatal */
  }
  return customer;
}

// ---------- UI polish ----------

async function _polishUi() {
  try {
    // Theme-aware status-bar sync — uses the canonical helper so the
    // light/dark choice always matches the JS theme. Replaces the old
    // hardcoded "#FAF7F1 + Style.Dark" combo that fought dark mode.
    const { syncNativeChromeToTheme } = await import("@/lib/statusBar");
    await syncNativeChromeToTheme();
  } catch {
    /* best effort */
  }
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 250 });
  } catch {
    /* best effort */
  }
}
